import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryClient';
import { useAuth } from '@/app/providers';
import type { Profile } from '@/types';
import { listCommunitiesByOwner } from '@/features/communities/api/communities';
import { isHandleAvailable, saveProfileSettings } from '../api/settings';
import { HANDLE_REGEX, type SettingsDraft } from '../settings';

/**
 * Persist a settings draft, then refresh the cached profile + AuthProvider so the
 * TopNav avatar/name and the profile page reflect the change immediately.
 */
export function useSaveSettings() {
  const qc = useQueryClient();
  const { refreshProfile } = useAuth();
  return useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: SettingsDraft }) =>
      saveProfileSettings(id, draft),
    onSuccess: async (profile: Profile) => {
      qc.setQueryData(queryKeys.profile(profile.id), profile);
      if (profile.handle) qc.setQueryData(queryKeys.profileByHandle(profile.handle), profile);
      await refreshProfile();
    },
  });
}

export type HandleStatus = 'idle' | 'invalid' | 'checking' | 'taken' | 'ok';

/**
 * Live username validation (OG `handleStatus`): format-check synchronously, then
 * debounce a uniqueness lookup. Empty → idle, bad format → invalid, otherwise
 * checking → ok/taken. The lookup excludes the user's own row, so an unchanged
 * handle resolves to `ok`.
 */
export function useHandleAvailability(handle: string, selfId: string | undefined): HandleStatus {
  const [status, setStatus] = useState<HandleStatus>('idle');

  useEffect(() => {
    if (!handle) return setStatus('idle');
    if (!HANDLE_REGEX.test(handle)) return setStatus('invalid');
    if (!selfId) return setStatus('ok');

    let cancelled = false;
    setStatus('checking');
    const t = setTimeout(() => {
      isHandleAvailable(handle, selfId)
        .then((free) => !cancelled && setStatus(free ? 'ok' : 'taken'))
        .catch(() => !cancelled && setStatus('ok')); // network hiccup → don't block save
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [handle, selfId]);

  return status;
}

/** Communities the user owns — powers the settings "Communities" tab. */
export function useOwnedCommunities(ownerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.communities.byOwner(ownerId ?? ''),
    queryFn: () => listCommunitiesByOwner(ownerId!),
    enabled: Boolean(ownerId),
  });
}
